import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Flag, Coins, Users, FloppyDisk } from "@phosphor-icons/react";

const ALL_ROLES = ["admin", "directivo", "profesor", "jugador", "desarrollador"];

export default function AdminFeatureFlags() {
  const { user } = useAuth();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // key -> partial update

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/feature-flags");
      setFlags(r.data.flags || []);
      setEdits({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar flags");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const setEdit = (key, patch) => setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const save = async (key) => {
    const patch = edits[key];
    if (!patch || Object.keys(patch).length === 0) return;
    try {
      await api.put(`/admin/feature-flags/${key}`, patch);
      toast.success(`Flag "${key}" actualizado`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const toggleRole = (key, role, currentRoles) => {
    const next = currentRoles.includes(role)
      ? currentRoles.filter((r) => r !== role)
      : [...currentRoles, role];
    setEdit(key, { allowed_roles: next });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-feature-flags-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2">
          <Flag size={14} weight="bold" /> Admin · Feature Flags
        </p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Control de Funciones</h1>
        <p className="text-sm text-gray-400 mt-1">Activa/desactiva funciones experimentales por rol. Configura costo en DYD.</p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando flags...</div>
      ) : (
        <div className="space-y-4">
          {flags.map((f) => {
            const edit = edits[f.key] || {};
            const enabled = edit.enabled !== undefined ? edit.enabled : f.enabled;
            const roles = edit.allowed_roles !== undefined ? edit.allowed_roles : (f.allowed_roles || []);
            const dydCost = edit.dyd_cost !== undefined ? edit.dyd_cost : (f.dyd_cost || 0);
            const dirty = Object.keys(edit).length > 0;
            return (
              <div key={f.key} className="bg-[#141414] border border-white/10 rounded-lg p-5" data-testid={`ff-card-${f.key}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white">{f.name}</h3>
                      <Badge className="bg-white/10 text-gray-300 border-white/20 text-[9px] font-mono">{f.key}</Badge>
                      {enabled ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[9px]">ACTIVO</Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[9px]">INACTIVO</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{f.description}</p>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={(v) => setEdit(f.key, { enabled: v })}
                    data-testid={`ff-toggle-${f.key}`}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Roles */}
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2 flex items-center gap-1">
                      <Users size={12} /> Roles permitidos
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_ROLES.map((role) => {
                        const active = roles.includes(role);
                        return (
                          <button
                            key={role}
                            type="button"
                            onClick={() => toggleRole(f.key, role, roles)}
                            className={`text-[10px] tracking-wider uppercase font-bold px-2.5 py-1 rounded border transition-colors ${active ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-transparent text-gray-400 border-white/20 hover:border-[#CCFF00]/50"}`}
                            data-testid={`ff-role-${f.key}-${role}`}
                          >
                            {role}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* DYD cost */}
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2 flex items-center gap-1">
                      <Coins size={12} /> Costo DYD por uso
                    </div>
                    <Input
                      type="number"
                      min="0"
                      value={dydCost}
                      onChange={(e) => setEdit(f.key, { dyd_cost: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="bg-black border-white/10 text-white"
                      data-testid={`ff-cost-${f.key}`}
                    />
                    <p className="text-[10px] text-gray-500 mt-1">0 = gratis</p>
                  </div>
                </div>

                {dirty && (
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEdits((p) => { const n = { ...p }; delete n[f.key]; return n; })} className="border-white/10" data-testid={`ff-cancel-${f.key}`}>
                      Cancelar
                    </Button>
                    <Button onClick={() => save(f.key)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid={`ff-save-${f.key}`}>
                      <FloppyDisk size={14} className="mr-1" /> Guardar
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
